import { CancelTokenSource } from 'axios'
import { IRefDataDTO } from '../../models/Common'
import {
  EnquiryEmployerFormFieldMapping,
  EnquiryEmployerNoLongerEmployeedFormFieldMapping,
  EnquiryExistingEmployeeFormFieldMapping,
  EnquiryFormFieldMapping,
  EnquiryIndContractorFormFieldMapping,
  EnquirySBEFormFieldMapping,
  IEnquiry,
  IEnquiryDetail,
} from '../../models/Enquiry'
import EnquiryService from '../../services/EnquiryService'
import ProfileService from '../../services/ProfileService'

export const getEnquiry = async (id: string) => {
  return await EnquiryService.getEnquiryDetails(id).then((response) => {
    return response.result
  })
}

export const fetchCurrentUserProfile = async (source: CancelTokenSource) => {
  return await ProfileService.getCurrentUserProfile(source).then((response) => {
    return response.result
  })
}

export const getUpdateUserViewModel = async (source: CancelTokenSource) => {
  return await ProfileService.getUpdateUserViewModel(source).then(
    (response) => {
      return response.result
    }
  )
}

export const findRefNameById = (options: IRefDataDTO[], id: any) => {
  if (options === null || options === undefined) {
    return ''
  }
  const selection = options.find((item) => item.id === id)
  if (selection) {
    return selection.name
  }
  return ''
}

export const findRefNameArrayById = (options: IRefDataDTO[], ids: any) => {
  const arr: string[] = []
  if (options === undefined || ids === undefined) {
    return arr
  }
  for (let i = 0; i < ids.length; i++) {
    const value = findRefNameById(options, ids[i])
    if (value && value.length > 0) {
      arr.push(value)
    }
  }
  return arr
}

export const getBooleanText = (value: boolean) => {
  if (value === null || value === undefined) {
    return ''
  }

  return value ? 'Yes' : 'No'
}

export const getEnquiryLabel = (label: string, enquiry?: IEnquiry) => {
  if (enquiry === null || enquiry === undefined) {
    return EnquiryFormFieldMapping.get(label)
  }

  if (enquiry?.isEE && EnquiryExistingEmployeeFormFieldMapping.has(label)) {
    return EnquiryExistingEmployeeFormFieldMapping.get(label)
  }
  if (
    enquiry?.isenle &&
    EnquiryEmployerNoLongerEmployeedFormFieldMapping.has(label)
  ) {
    return EnquiryEmployerNoLongerEmployeedFormFieldMapping.get(label)
  }
  if (enquiry?.isEmployeer && EnquiryEmployerFormFieldMapping.has(label)) {
    return EnquiryEmployerFormFieldMapping.get(label)
  }
  if (enquiry?.isSBE && EnquirySBEFormFieldMapping.has(label)) {
    return EnquirySBEFormFieldMapping.get(label)
  }
  if (enquiry?.isIC && EnquiryIndContractorFormFieldMapping.has(label)) {
    return EnquiryIndContractorFormFieldMapping.get(label)
  }

  return EnquiryFormFieldMapping.get(label)
}

export const getEnquiryDetailLabel = (
  label: string,
  enquiry?: IEnquiryDetail
) => {
  if (enquiry === null || enquiry === undefined) {
    return EnquiryFormFieldMapping.get(label)
  }

  if (enquiry?.isEE && EnquiryExistingEmployeeFormFieldMapping.has(label)) {
    return EnquiryExistingEmployeeFormFieldMapping.get(label)
  }
  if (
    enquiry?.isenle &&
    EnquiryEmployerNoLongerEmployeedFormFieldMapping.has(label)
  ) {
    return EnquiryEmployerNoLongerEmployeedFormFieldMapping.get(label)
  }
  if (enquiry?.isEmployeer && EnquiryEmployerFormFieldMapping.has(label)) {
    return EnquiryEmployerFormFieldMapping.get(label)
  }
  if (enquiry?.isSBE && EnquirySBEFormFieldMapping.has(label)) {
    return EnquirySBEFormFieldMapping.get(label)
  }
  if (enquiry?.isIC && EnquiryIndContractorFormFieldMapping.has(label)) {
    return EnquiryIndContractorFormFieldMapping.get(label)
  }

  return EnquiryFormFieldMapping.get(label)
}

export const stringifyErrors = (
  errors: any,
  addErrors: any,
  enquiry?: IEnquiry
) => {
  const keys = Object.keys(errors)

  const formatted = keys?.map((key) => {
    const error = getEnquiryLabel(key, enquiry)
    const errMsg = errors[key]

    if (errMsg.includes('Australian Business Number is invalid')) {
      return `${error}: ${errMsg}`
    } else if (errMsg.includes('Please enter a valid phone number')) {
      return `${error}: ${errMsg}`
    } else {
      return `${error}: cannot be empty, it is required.`
    }
  })

  addErrors(formatted)
}

export const getIds = (values: any) => {
  let ids: string[] = []
  if (values['employmentTypeId'] && values['employmentTypeId'].length > 0) {
    ids.push(values['employmentTypeId'])
  }
  if (values['paidLeaveId'] && values['paidLeaveId'].length > 0) {
    ids.push(values['paidLeaveId'])
  }
  if (
    values['apprenticeOrTraineeId'] &&
    values['apprenticeOrTraineeId'].length > 0
  ) {
    ids.push(values['apprenticeOrTraineeId'])
  }
  if (
    values['independentContractorId'] &&
    values['independentContractorId'].length > 0
  ) {
    ids.push(values['independentContractorId'])
  }
  if (
    values['genuineContractEmployeeTypeId'] &&
    values['genuineContractEmployeeTypeId'].length > 0
  ) {
    ids.push(values['genuineContractEmployeeTypeId'])
  }
  if (
    values['personDescriptionIds'] &&
    values['personDescriptionIds'].length > 0
  ) {
    ids = ids.concat(values['personDescriptionIds'])
  }

  if (values['visaTypeId'] && values['visaTypeId'].length > 0) {
    ids.push(values['visaTypeId'])
  }
  if (values['countryId'] && values['countryId'].length > 0) {
    ids.push(values['countryId'])
  }
  if (values['officeLocationId'] && values['officeLocationId'].length > 0) {
    ids.push(values['officeLocationId'])
  }
  if (values['languageId'] && values['languageId'].length > 0) {
    ids.push(values['languageId'])
  }
  if (
    values['notSpokenToEmployerReasonId'] &&
    values['notSpokenToEmployerReasonId'].length > 0
  ) {
    ids.push(values['notSpokenToEmployerReasonId'])
  }
  if (
    values['areOthersAffectedId'] &&
    values['areOthersAffectedId'].length > 0
  ) {
    ids.push(values['areOthersAffectedId'])
  }
  if (
    values['supportingEvidenceIds'] &&
    values['supportingEvidenceIds'].length > 0
  ) {
    ids = ids.concat(values['supportingEvidenceIds'])
  }
  return ids
}
